import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { generatePath, useNavigate } from 'react-router';
import type { CardMenuItem } from '@tempmee/core';
import { Box, Link, OverflowMenu } from '@tempmee/react';
import { AdditionalDetails, LargeShiftDetails, Receipt } from '@tempmee/react-blocks';
import { OutlineHeartIcon, OutlineXCircleErrorIcon, OutlineXCircleIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { addFavoriteProfessional, blockProfessional } from '~/actions';
import LegacyJobDetails from '~/components/JobDetails/JobDetails';
import { FavoriteAddedPopup } from '~/components/ProfessionalsHub/components/shared/FavoriteAddedPopup';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { jobCompletedByToday } from '~/components/JobDetails/utils';
import { useMediaQuery } from '~/hooks';
import { useAppSelector } from '~/hooks/useAppSelector';
import type { IUser } from '~/models';
import { routes } from '~/routes';
import { getProfessionalName } from '~/utils/professional';
import { ShiftDetailsPageShell } from '../ShiftDetailsPageShell';
import { useBookedProfessionalCredentials } from '../shared/hooks/useBookedProfessionalCredentials';
import { useCompletedFeedbackRedirect } from './hooks/useCompletedFeedbackRedirect';
import type { IShiftDetailsPageProps } from '../shiftDetailsPages';
import { EM_DASH, toAdditionalDetailsRows } from '../shared/utils/shiftPanelsProps';
import { CompletedProfessionalPanel } from './components/CompletedProfessionalPanel';
import { useCompletedProfessional } from './hooks/useCompletedProfessional';
import { useFavoriteAddedPopup } from './hooks/useFavoriteAddedPopup';
import { hasTransactionHistory } from '../shared/utils/transactionHistory';
import { COLLAPSE_BREAKPOINT } from '../shared/utils/layout';
import {
  toCompletedReceiptLines,
  toCompletedReceiptTotal,
  toCompletedShiftSummary,
  type TCompletedShiftJob,
} from './utils/completedShiftView';


const BLOCK_DESCRIPTION =
  'Are you sure you want to block this professional from working with you again?';

const BLOCK_FAVORITE_DESCRIPTION = (name: string) =>
  `To block this professional, they must be removed from your Favorites list. Would you like to ` +
  `continue with removing ${name} from your Favorites and blocking them?`;

/**
 * The confirm popup renders whatever `Icon` returns with no sizing of its own, and
 * the icons default to 24px — too small to read as the popup's subject. 80px with
 * a bottom rule is the size every other destructive confirm in the app uses.
 */
const POPUP_ICON_SIZE = 80;
const POPUP_ICON_WRAPPER_STYLE = { marginBottom: 20 };

const UNABLE_TO_FAVORITE_DESCRIPTION =
  'To add this professional to your Favorites, please contact Customer Support to remove this ' +
  'professional from your blocked list.';

/**
 * The redesigned COMPLETED Shift Details page body.
 *
 * The professional who worked the shift heads the primary column, with their full
 * profile beneath; the sidebar carries the shift summary, its attributes and the
 * receipt. Reached through `ShiftDetailsRedesignGate`, which has already loaded
 * the job and matched its status, so there is no loading or not-found state here.
 *
 * Both sidebar panels below the summary ship **collapsed**, per the Figma's 56px
 * headers and the same decision the other states took.
 */
function CompletedShiftDetailsRedesign({ job }: IShiftDetailsPageProps) {
  const completedShift = job as TCompletedShiftJob;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isCollapsed] = useMediaQuery([COLLAPSE_BREAKPOINT]);

  /*
   * The route entry guard, first: the legacy screen redirects a completed shift
   * whose feedback is still outstanding into the rating flow, and the rating is
   * what releases the professional's payment. It runs before any of this page's
   * own data work for the same reason the legacy runs it in `componentDidUpdate`
   * — the office is meant to land on rating, not here.
   */
  useCompletedFeedbackRedirect(job);

  const { professional } = useCompletedProfessional(job);

  const user = useAppSelector((store) => store.user.user) as IUser | null;

  /*
   * The cancelled page's hook: a terminal shift names its professional, so there
   * is no applicant pool to search — this asks the credentials domain directly.
   */
  const { authorizations } = useBookedProfessionalCredentials({
    job,
    professional: professional ?? job.offer_final_reciever,
  });

  const favoriteAdded = useFavoriteAddedPopup();
  const [isBlockConfirmOpen, setIsBlockConfirmOpen] = useState(false);
  const [isUnableToFavoriteOpen, setIsUnableToFavoriteOpen] = useState(false);

  const professionalId = job.offer_final_reciever?.id;
  const professionalName = getProfessionalName(job.offer_final_reciever) ?? 'this professional';

  /*
   * `favoriteProfessionals` holds professional ids at runtime, though `IUser`
   * types it as `IUser[]` — the same cast `JobSummaryRating` documents.
   */
  const favoriteProfessionalIds = (user?.favoriteProfessionals ?? []) as unknown as string[];

  const isFavorite = Boolean(
    job.offer_final_reciever?.isFavorite ||
      (professionalId && favoriteProfessionalIds.includes(professionalId)),
  );
  const isBlocked = Boolean(
    professionalId && user?.blocked_hygienist?.includes(professionalId),
  );

  /*
   * The legacy gate, reproduced: favourite and block are offered on a completed
   * shift unless it finished today. `jobCompletedByToday` is the legacy's own
   * helper, so the two paths cannot drift on what "today" means.
   */
  const canManageProfessional = Boolean(professionalId) && !jobCompletedByToday(job);

  const professionalActions: CardMenuItem[] = useMemo(() => {
    if (!canManageProfessional) {
      return [];
    }

    return [
      {
        label: isFavorite ? 'Added to Favorites' : 'Add to Favorites',
        icon: <OutlineHeartIcon />,
        testID: 'completed-favorite-action',
        disabled: isFavorite,
        onSelect: () => {
          /*
           * A blocked professional cannot be favourited, and the legacy explains
           * why rather than failing silently.
           */
          if (isBlocked) {
            setIsUnableToFavoriteOpen(true);
            return;
          }

          dispatch(addFavoriteProfessional(professionalId));
        },
      },
      {
        label: isBlocked ? 'Blocked Professional' : 'Block Professional',
        icon: <OutlineXCircleIcon />,
        testID: 'completed-block-action',
        disabled: isBlocked,
        onSelect: () => setIsBlockConfirmOpen(true),
      },
    ];
  }, [canManageProfessional, isFavorite, isBlocked, dispatch, professionalId]);

  return (
    <>
      <ShiftDetailsPageShell
        title="Completed Shift"
        /*
         * The page header's own `⋯` is left empty, which hides its trigger. Every
         * action a completed shift offers acts on the professional, so they belong
         * to the topper's menu — the Figma draws a trigger in both places but
         * specifies items for neither, and duplicating the same two entries would
         * give the office two menus that do the same thing.
         */
        summary={<LargeShiftDetails {...toCompletedShiftSummary(completedShift)} />}
        primary={
          <CompletedProfessionalPanel
            job={job}
            professional={professional}
            authorizations={authorizations}
            isCollapsed={isCollapsed}
            /*
             * `undefined` rather than a menu that renders nothing: the topper
             * pins a slot for whatever it is handed, so an empty menu would
             * still reserve the corner it sits in.
             */
            overflowMenu={
              professionalActions.length > 0 ? (
                <OverflowMenu
                  items={professionalActions}
                  label="Professional actions"
                  // The Figma's 40px corner trigger; `large` is the page-header size.
                  size="medium"
                  testID="completed-professional-actions"
                />
              ) : undefined
            }
          />
        }
        sidebar={
          <>
            <AdditionalDetails
              variant="scheduled-complete"
              {...toAdditionalDetailsRows(
                completedShift,
                // The shift was confirmed by somebody, so the row stays even when
                // the payload does not name them — an em dash, not a dropped row.
                getProfessionalName(completedShift.confirmPerformedBy) || EM_DASH,
              )}
              defaultOpen={false}
            />
            <Receipt
              lines={toCompletedReceiptLines(completedShift)}
              total={toCompletedReceiptTotal(completedShift)}
              defaultOpen={false}
              totalAction={
                hasTransactionHistory(completedShift) ? (
                  <Link
                    as="button"
                    /*
                     * `as="button"` with `useNavigate`, not an `href`: the DS
                     * `Link` renders a real anchor, and an in-portal href would
                     * reload the whole SPA.
                     */
                    onClick={() =>
                      navigate(generatePath(routes.transactionHistoryDetails, { jobId: job.id }))
                    }
                    data-testid="transaction-history-link"
                    data-cy="transaction-history-link"
                  >
                    Transaction History
                  </Link>
                ) : undefined
              }
            />
          </>
        }
      />

      {isBlockConfirmOpen && (
        <ConfirmPopup
          title="Block Professional"
          description={
            isFavorite ? BLOCK_FAVORITE_DESCRIPTION(professionalName) : BLOCK_DESCRIPTION
          }
          leftButtonText="Go Back"
          leftButtonAction={() => setIsBlockConfirmOpen(false)}
          rightButtonText="Continue"
          rightButtonAction={() => {
            setIsBlockConfirmOpen(false);
            dispatch(blockProfessional({ user: job.offer_final_reciever }));
          }}
          Icon={() => (
            <Box style={POPUP_ICON_WRAPPER_STYLE} data-testid="block-popup-icon">
              <OutlineXCircleErrorIcon size={POPUP_ICON_SIZE} color={colors.error[500]} />
            </Box>
          )}
          closePopup={() => setIsBlockConfirmOpen(false)}
        />
      )}

      {favoriteAdded.isOpen && (
        <FavoriteAddedPopup professionalName={professionalName} onClose={favoriteAdded.close} />
      )}

      {isUnableToFavoriteOpen && (
        <ConfirmPopup
          title="Unable to Add to Favorites"
          description={UNABLE_TO_FAVORITE_DESCRIPTION}
          rightButtonText="Close"
          rightButtonAction={() => setIsUnableToFavoriteOpen(false)}
          closePopup={() => setIsUnableToFavoriteOpen(false)}
        />
      )}
    </>
  );
}

/**
 * COMPLETED Shift Details, as registered in `SHIFT_DETAILS_PAGES`.
 *
 * A finished onsite interview is a different page: the legacy screen titles it
 * "Interview Summary" and offers the permanent-hire Hire/Reject decision, none of
 * which the shift redesign has a frame for. Until it does, completed interviews
 * stay on the screen they have today.
 *
 * The split is a component boundary rather than an early return so this page's
 * feedback redirect and professional fetch never run on the delegated path, where
 * the legacy screen runs its own.
 */
export function CompletedShiftDetailsPage({ job }: IShiftDetailsPageProps) {
  if (job.isOnsiteInterview) {
    return <LegacyJobDetails />;
  }

  return <CompletedShiftDetailsRedesign job={job} />;
}
